import { Capacitor } from '@capacitor/core';
import { outboxClear, recoveryFlagSet } from './outbox';

// ── Platform helpers ──────────────────────────────────────────

/**
 * Get the API base URL for the current platform.
 * - Web: empty string (uses relative URLs — Vite proxy or same-origin)
 * - Native: from VITE_API_BASE_URL env var
 */
function getApiBaseUrl() {
  if (Capacitor.isNativePlatform()) {
    return import.meta.env.VITE_API_BASE_URL || 'https://julynote.app';
  }
  return '';
}

export function redirectToLogin() {
  // Mark that the session expired mid-session, so any surviving draft is
  // treated as recoverable — not as a spurious leftover from a finished edit.
  recoveryFlagSet();

  // Fire session-expiring so the open block editor flushes its draft.
  window.dispatchEvent(new CustomEvent('session-expiring'));

  // Ask AppLayout (root) to show a "session expired" confirm dialog.  The
  // actual token clear + redirect happens there, after the user confirms
  // and the draft has been flushed — the edit-in-progress is never lost.
  window.dispatchEvent(new CustomEvent('login-expired'));
}

// ── Token expiry helpers ──────────────────────────────────────

/**
 * Return milliseconds until the stored access token expires.
 * Returns 0 if there is no token or it has already expired.
 */
function getTokenExpiresInMs() {
  const token = localStorage.getItem('access_token');
  if (!token) return 0;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return Math.max(0, payload.exp * 1000 - Date.now());
  } catch {
    return 0;
  }
}

/**
 * Schedule a proactive token refresh ~5 minutes before expiry.
 * Call once on app mount — re-schedules itself after each attempt, bounded
 * by a retry cap so a permanently-invalid token doesn't retry forever.
 */
const MAX_REFRESH_FAILURES = 5;           // stop re-arming after this many consecutive failures
const REFRESH_RETRY_DELAY_MS = 60 * 1000; // backoff between failed refresh attempts
let consecutiveRefreshFailures = 0;

export function scheduleTokenRefresh() {
  const remaining = getTokenExpiresInMs();
  if (remaining <= 0) return; // already expired — nothing to schedule

  // Refresh 5 minutes before expiry
  const refreshInMs = remaining - 5 * 60 * 1000;
  const delay = Math.max(0, refreshInMs);

  setTimeout(() => {
    tryRefreshToken().then(newToken => {
      if (newToken) {
        consecutiveRefreshFailures = 0;
        scheduleTokenRefresh();
        return;
      }
      consecutiveRefreshFailures += 1;
      if (consecutiveRefreshFailures < MAX_REFRESH_FAILURES) {
        // Re-arm after a short backoff — the failure may be transient.
        setTimeout(scheduleTokenRefresh, REFRESH_RETRY_DELAY_MS);
      }
    });
  }, delay);
}

/**
 * Register a visibility-change listener that refreshes the token
 * when the user returns to the tab and the token is close to expiry.
 * Call once on app mount.
 */
export function setupVisibilityRefresh() {
  if (typeof document === 'undefined') return;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    const remaining = getTokenExpiresInMs();
    // Refresh when within 30 minutes of expiry OR already expired — a tab
    // left backgrounded/sleeping is exactly when recovery is needed. The
    // refresh itself is still guarded by refresh_token presence.
    if (remaining < 30 * 60 * 1000) {
      tryRefreshToken();
    }
  });
}

// ── Auth (email + password) ───────────────────────────────────

/**
 * Login with email and password.
 * Stores access_token, refresh_token, username, and user_id in localStorage.
 */
export async function loginUser(email, password) {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/api/base/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.detail || 'Login failed');
  }

  const data = await response.json();

  localStorage.setItem('access_token', data.access_token);
  if (data.refresh_token) {
    localStorage.setItem('refresh_token', data.refresh_token);
  }
  localStorage.setItem('username', data.username);
  if (data.user_id) {
    localStorage.setItem('user_id', data.user_id);
  }

  return data;
}

/**
 * Register by email only. A verification link is sent —
 * the user sets their password when they verify.
 */
export async function registerUser(email) {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/api/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.detail || 'Registration failed');
  }

  return response.json();
}

/**
 * Check if a one-time code is valid (does NOT consume it).
 * Returns { valid: boolean, type: 'verify' | 'reset' | '' }.
 */
export async function checkCode(code) {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/api/auth/check-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.detail || 'Code check failed');
  }

  return response.json();
}

/**
 * Verify email with a token, optionally setting a password.
 * Pass `password` to set the password as part of verification
 * (first-time account setup).
 */
export async function verifyEmail(token, password = null) {
  const baseUrl = getApiBaseUrl();
  const body = { token };
  if (password) body.password = password;

  const response = await fetch(`${baseUrl}/api/auth/verify-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.detail || 'Email verification failed');
  }

  return response.json();
}

/**
 * Request a password-reset email.
 */
export async function forgotPassword(email) {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/api/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.detail || 'Request failed');
  }

  return response.json();
}

/**
 * Resend a verification or reset code to the given email.
 * @param {string} email
 * @param {'verify'|'reset'} type
 */
export async function resendCode(email, type) {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/api/auth/resend-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, type }),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.detail || 'Failed to resend code');
  }

  return response.json();
}

/**
 * Reset password using a reset token.
 */
export async function resetPassword(token, password) {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/api/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, password }),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.detail || 'Password reset failed');
  }

  return response.json();
}

// ── Token refresh ──────────────────────────────────────────────

/**
 * Try to refresh the access token using a stored refresh_token.
 * On success the new access_token is written back to localStorage.
 *
 * @returns {string|null} the new access_token, or null if refresh failed
 */
async function tryRefreshToken() {
  const rt = localStorage.getItem('refresh_token');
  if (!rt) return null;
  try {
    const baseUrl = getApiBaseUrl();
    const resp = await fetch(`${baseUrl}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: rt }),
    });
    if (!resp.ok) return null;
    const data = await resp.json();
    localStorage.setItem('access_token', data.access_token);
    return data.access_token;
  } catch {
    return null;
  }
}

/**
 * Ensure the stored access token is usable, attempting a refresh if it has
 * expired. Used by the route guard so closing/reopening the app past the
 * access token's lifetime doesn't force a re-login while the refresh token
 * is still valid.
 * @returns {Promise<boolean>}
 */
export async function ensureAuthenticated() {
  if (isTokenValid()) return true;
  const token = await tryRefreshToken();
  return !!token;
}

// ── Fetch wrapper ─────────────────────────────────────────────

/**
 * Fetch wrapper that includes authentication token.
 * On native, prepends VITE_API_BASE_URL to relative paths.
 */
export async function fetchWithAuth(url, options = {}) {
  const token = localStorage.getItem('access_token');

  const headers = {
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Resolve full URL: platform-aware base URL for native, relative for web
  const baseUrl = getApiBaseUrl();
  const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;

  const response = await fetch(fullUrl, {
    ...options,
    headers,
  });

  // If unauthorized, try a silent token refresh before redirecting
  if (response.status === 401) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      headers['Authorization'] = `Bearer ${refreshed}`;
      return fetch(fullUrl, { ...options, headers });
    }
    redirectToLogin();
  }

  return response;
}

/**
 * Clear all stored credentials and redirect to login.
 * Firebase sign-out on native (safe no-op import — only runs if capacitorFirebase is loaded).
 */
export async function clearSessionAndRedirect({ wipeCache = true } = {}) {
  if (Capacitor.isNativePlatform()) {
    try {
      const { firebaseSignOut } = await import('../capacitorFirebase');
      await firebaseSignOut();
    } catch (e) {
      console.warn('Firebase sign-out skipped:', e);
    }
  }

  // On an explicit sign-out / account switch, wipe this user's local offline
  // cache (outbox + editor draft) before clearing the auth token.  On session
  // expiry we skip the wipe so the same user's in-progress edit is preserved
  // across re-login — cross-account isolation is already guaranteed by the
  // per-user namespace in outbox.js.  outboxClear() also removes the draft key.
  if (wipeCache) {
    try {
      await outboxClear();
    } catch (e) {
      console.warn('Local cache wipe failed:', e);
    }
  }

  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('username');
  localStorage.removeItem('login_provider');
  localStorage.removeItem('user_id');
  localStorage.removeItem('julynote_focused_folder');

  if (Capacitor.isNativePlatform()) {
    window.location.hash = '#/login';
  } else {
    window.location.href = '/login';
  }
}

/**
 * Logout user — call backend, Firebase sign-out (native), then clear all stored credentials.
 */
export async function logout() {
  try {
    await fetchWithAuth('/api/base/logout', { method: 'POST' });
  } catch {
    // Proceed with local cleanup even if the backend call fails
  }

  await clearSessionAndRedirect();
}

/**
 * Delete the current user's own account (self-service).
 * Backend deletes the user row + Firebase Auth account; then clear credentials
 * and redirect to login. Throws on failure so the UI can show the error.
 */
export async function deleteAccount() {
  const response = await fetchWithAuth('/api/user/me', { method: 'DELETE' });

  if (!response.ok) {
    const err = await response.json();
    const error = new Error(err.detail || 'Failed to delete account');
    error.status = response.status;
    throw error;
  }

  await clearSessionAndRedirect();
}

/**
 * Get current username from localStorage
 */
export function getCurrentUsername() {
  return localStorage.getItem('username');
}

/**
 * Returns true if there is a non-expired access token in localStorage.
 * Decodes the JWT payload locally (no network call) to check the `exp` claim.
 */
export function isTokenValid() {
  const token = localStorage.getItem('access_token');
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

// ── File API ──────────────────────────────────────────────────

/**
 * Get the full raw content of a file.
 * @param {string} path - Repo-relative file path
 * @param {{ refresh?: boolean }} [options] - set `refresh: true` to bypass the
 *   backend cache and re-read from the Git provider (pull-to-refresh).
 * @returns {Promise<{path: string, exists: boolean, content: string, sha?: string, version?: number, note?: string}>}
 */
export async function fileGetContent(path = '', options = {}) {
  const { refresh = false } = options;
  const params = new URLSearchParams();
  if (path) params.set('path', path);
  if (refresh) params.set('refresh', 'true');
  const qs = params.toString();
  const response = await fetchWithAuth(`/api/file/content${qs ? `?${qs}` : ''}`);

  if (!response.ok) {
    const err = await response.json();
    const error = new Error(err.detail || 'Failed to read file content');
    error.status = response.status;
    throw error;
  }

  return response.json();
}

/**
 * Save (create or update) a file with full content.
 * @param {string} path - Repo-relative file path
 * @param {string} content - Full markdown content
 * @param {string|null} sha - Blob SHA from a prior GET /content (null for new files)
 * @param {number|null} version - Cache version from a prior GET /content (null = skip check)
 * @returns {Promise<{message: string, commit_sha: string, file_path: string, version?: number}>}
 */
export async function fileSaveContent(path, content, sha = null, version = null, isReorder = false, timeline = null) {
  const body = { path, content, sha, version, is_reorder: isReorder };
  if (timeline) {
    body.timeline = timeline;
  }
  const response = await fetchWithAuth('/api/file/save', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    keepalive: true,
  });

  if (!response.ok) {
    const err = await response.json();
    const detail = err.detail;
    const error = new Error(
      typeof detail === 'string' ? detail : (detail?.message || 'Failed to save file')
    );
    error.status = response.status;
    error.detail = detail; // full body — the outbox reads {current_sha, current_content} on 409
    throw error;
  }

  return response.json();
}

/**
 * Batch save — save multiple files in one request.
 * @param {Array<{path: string, content: string, sha: string|null}>} files
 * @returns {Promise<{message: string, results: Array<{file_path: string, success: boolean, commit_sha: string, error: string}>}>}
 */
export async function fileBatchSave(files) {
  const response = await fetchWithAuth('/api/file/batch-save', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ files }),
    keepalive: true,
  });

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Batch save failed');
  }

  return response.json();
}

/**
 * Delete a file or folder from the remote repository.
 * @param {string} path - Repo-relative path to delete
 * @returns {Promise<{message: string, commit_sha: string, file_path: string}>}
 */
export async function fileDelete(path) {
  const response = await fetchWithAuth('/api/file/delete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ path }),
  });

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to delete');
  }

  return response.json();
}

// ── Conflict resolution ─────────────────────────────────────────

/**
 * List files with an external-edit conflict on the server.
 * @returns {Promise<{conflicts: Array<{path: string, conflict_type: string}>}>}
 */
export async function fileConflicts() {
  const response = await fetchWithAuth('/api/file/conflicts');
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to list conflicts');
  }
  return response.json();
}

/**
 * Batch status for a set of paths — foreground sync polling.
 * @param {string[]} paths - Repo-relative paths to check
 * @returns {Promise<{items: Array<{path: string, state: string}>}>}
 */
export async function fileStatus(paths) {
  const response = await fetchWithAuth('/api/file/status', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ paths }),
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to check file status');
  }
  return response.json();
}

/**
 * Fetch the three-way inputs (base/ours/theirs) for one external-edit conflict.
 * @param {string} path - Repo-relative file path
 * @returns {Promise<{path: string, exists: boolean, base_content: string|null, ours_content: string, theirs_content: string, remote_blob_sha: string|null}>}
 */
export async function fileConflictDetail(path) {
  const params = new URLSearchParams({ path });
  const response = await fetchWithAuth(`/api/file/conflict-detail?${params.toString()}`);
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to load conflict detail');
  }
  return response.json();
}

/**
 * Write back a merged result for an external-edit conflict.
 * @param {string} path - Repo-relative file path
 * @param {string} content - Merged content (or theirs, for "use all server")
 * @param {string|null} remoteBlobSha - Current Git blob sha (optimistic lock)
 * @returns {Promise<{message: string, file_path: string, sha: string|null}>}
 */
export async function resolveExternalConflict(path, content, remoteBlobSha = null) {
  const response = await fetchWithAuth('/api/file/resolve-external-conflict', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ path, content, remote_blob_sha: remoteBlobSha }),
    keepalive: true,
  });
  if (!response.ok) {
    const err = await response.json();
    const detail = err.detail;
    const error = new Error(
      typeof detail === 'string' ? detail : (detail?.message || 'Failed to resolve conflict')
    );
    error.status = response.status;
    error.detail = detail;
    throw error;
  }
  return response.json();
}

// ── Timeline ─────────────────────────────────────────────────

/**
 * Fetch timeline date entries (current + prev/next) from a folder.
 * Works with any folder containing YYYY-MM-DD.md files — timeline
 * and checklist node folders.
 * @param {string} folderPath - folder path (e.g. "Timeline")
 * @param {string} date - target date YYYY-MM-DD (empty = today)
 * @returns {Promise<{message: string, folder_path: string, current: object|null, prev: object|null, next: object|null}>}
 */
export async function fetchTimeline(folderPath, date = '') {
  const response = await fetchWithAuth('/api/file/buffer-timeline', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ folder_path: folderPath, date }),
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to fetch timeline');
  }
  return response.json();
}

/**
 * Fetch a range of timeline date entries for continuous scroll.
 * @param {string} folderPath - folder path (e.g. "Timeline")
 * @param {string} cursorDate - anchor date YYYY-MM-DD
 * @param {string} direction - "before" or "after"
 * @param {number} limit - max dates to return (1-20)
 * @returns {Promise<{message: string, folder_path: string, entries: object[], has_more: boolean}>}
 */
export async function fetchBufferRange(folderPath, cursorDate, direction = 'before', limit = 5) {
  const response = await fetchWithAuth('/api/file/buffer-range', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      folder_path: folderPath,
      cursor_date: cursorDate,
      direction,
      limit,
    }),
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to fetch buffer range');
  }
  return response.json();
}

// ── User Data Source Settings ─────────────────────────────────

/**
 * Get the user's last active node file path.
 * @returns {Promise<{message: string, active_node: string}>}
 */
export async function getActiveNode() {
  const response = await fetchWithAuth('/api/node/active_node');
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to get active node');
  }
  return response.json();
}

/**
 * Persist the active node file path for this user.
 * @param {string} path - Repo-relative file path
 * @returns {Promise<{message: string, active_node: string}>}
 */
export async function setActiveNode(path) {
  const response = await fetchWithAuth('/api/node/active_node', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ path }),
  });

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to set active node');
  }

  return response.json();
}

/**
 * Get the current user's data source settings (token never returned).
 * @returns {Promise<{message: string, data_source: {provider: string, repo_name: string, access_token: string}}>}
 */
export async function getUserSettings() {
  const response = await fetchWithAuth('/api/user/settings');
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to get settings');
  }
  return response.json();
}

/**
 * Update the current user's display name.
 * @param {string} name
 * @returns {Promise<{message: string, name: string}>}
 */
export async function updateProfileName(name) {
  const response = await fetchWithAuth('/api/user/settings/profile', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to update name');
  }
  return response.json();
}

/**
 * Save data source settings AND test the connection.
 * @param {string} provider - "gitee" or "github"
 * @param {string} repoName - e.g. "owner/repo"
 * @param {string} accessToken
 * @param {string} branch - explicit branch; "" = follow the repo's default
 * @returns {Promise<{message: string, data_source: {provider: string, repo_name: string, access_token: string, branch: string}, test_result?: {success: boolean, message: string, user_login: string}}>}
 */
export async function updateUserSettings(provider, repoName, accessToken, branch = '') {
  const response = await fetchWithAuth('/api/user/settings/data_source', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ provider, repo_name: repoName, access_token: accessToken, branch }),
  });

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to save settings');
  }

  return response.json();
}

/**
 * Save block edit auto-save preferences (trigger word + delay).
 * Sent independently from data source settings — only the two keys are updated.
 * @param {string} word - trigger word (max 20 chars, empty to reset to default)
 * @param {number} delay - wait time in seconds (clamped 0.5–10)
 * @returns {Promise<{message: string, save_settings: object}>}
 */
export async function saveUserBlockSettings(word, delay) {
  const response = await fetchWithAuth('/api/user/settings/save-settings', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ block_auto_save_word: word, block_auto_save_delay: delay }),
  });

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to save block settings');
  }

  return response.json();
}

/**
 * Fetch a help markdown document by name.
 * @param {string} name - document name without .md extension (e.g. "gitee_token")
 * @returns {Promise<{name: string, content: string}>}
 */
export async function fetchHelpDoc(name) {
  const response = await fetchWithAuth(`/api/user/help-doc/${encodeURIComponent(name)}`);
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to load help document');
  }
  return response.json();
}

/**
 * Send feedback to the app team.
 * @param {string} content - feedback text
 * @param {string} email - contact email (optional)
 * @returns {Promise<{message: string}>}
 */
export async function submitFeedback(content, email = '') {
  const response = await fetchWithAuth('/api/user/feedback', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content, email }),
  });

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to send feedback');
  }

  return response.json();
}

/**
 * List repos for a given provider + token (transient — does not read saved settings).
 * @param {string} provider - "gitee" or "github"
 * @param {string} accessToken - optional; if empty, backend resolves from the saved PAT
 * @returns {Promise<{message: string, repos: Array<{full_name: string, description: string, private: boolean}>}>}
 */
export async function listReposWithToken(provider, accessToken = '') {
  const response = await fetchWithAuth('/api/user/settings/list-repos', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ provider, access_token: accessToken }),
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to list repos');
  }
  return response.json();
}

/**
 * Create a new repository on the provider.
 * @param {string} provider - "gitee" or "github"
 * @param {string} name - repo name (e.g. "my-notes")
 * @param {string} accessToken - transient token; if empty, the backend resolves
 *                               it from the saved PAT
 * @returns {Promise<{message: string, full_name: string, private: boolean, write_verified: boolean}>}
 */
export async function createRepo(provider, name, accessToken = '') {
  const response = await fetchWithAuth('/api/user/settings/create-repo', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ provider, name, access_token: accessToken }),
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to create repo');
  }
  return response.json();
}

/**
 * Resolve a repo's default branch (for the branch hint in the setup UI).
 * @param {string} provider - "gitee" or "github"
 * @param {string} repoName - "owner/repo"
 * @param {string} accessToken - optional; if empty, backend resolves from the saved PAT
 * @returns {Promise<{default_branch: string}>}
 */
export async function getDefaultBranch(provider, repoName, accessToken = '') {
  const response = await fetchWithAuth('/api/user/settings/default-branch', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ provider, repo_name: repoName, access_token: accessToken }),
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to resolve default branch');
  }
  return response.json();
}

/**
 * List a repo's branches (for the branch picker in the setup UI).
 * @param {string} provider - "gitee" or "github"
 * @param {string} repoName - "owner/repo"
 * @param {string} accessToken - optional; if empty, backend resolves from the saved PAT
 * @returns {Promise<{message: string, branches: string[]}>}
 */
export async function listBranches(provider, repoName, accessToken = '') {
  const response = await fetchWithAuth('/api/user/settings/list-branches', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ provider, repo_name: repoName, access_token: accessToken }),
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to list branches');
  }
  return response.json();
}

// ── Node Tree & Settings ────────────────────────────────────────

/**
 * Fetch the node tree for a folder (no settings — see fetchNodeSettings).
 * @param {string} path - Folder path to list (empty = notes root, non-recursive)
 * @param {{ includeJson?: boolean }} [options] - set `includeJson: true` to
 *   also list .json blobs (JulySpace views) alongside .md notes.
 * @returns {Promise<{message: string, tree: Object, nodeTypes: Object}>}
 */
export async function fetchNodeTree(path = '', options = {}) {
  const { includeJson = false } = options;
  const params = new URLSearchParams();
  if (path) params.set('path', path);
  if (includeJson) params.set('include_json', 'true');
  const qs = params.toString();
  const response = await fetchWithAuth(`/api/node/tree${qs ? `?${qs}` : ''}`);
  if (!response.ok) {
    const err = await response.json();
    // 503 rate-limit responses carry detail as {message, retryable}, not a string.
    const detail = err.detail;
    const msg = detail && typeof detail === 'object' ? detail.message : detail;
    throw new Error(msg || 'Failed to fetch node tree');
  }
  return response.json();
}

/**
 * Fetch all per-node settings from node_setting.json.
 * @returns {Promise<{message: string, settings: Object}>}
 */
export async function fetchNodeSettings() {
  const response = await fetchWithAuth('/api/node/settings');
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to fetch node settings');
  }
  return response.json();
}

/**
 * Search files and folders by name (case-insensitive substring).
 * @param {string} q - search query
 * @returns {Promise<{message: string, results: Array<{path, name, type}>}>}
 */
export async function searchNodes(q) {
  const response = await fetchWithAuth(`/api/node/search?q=${encodeURIComponent(q)}`);
  if (!response.ok) {
    const err = await response.json();
    // 503 rate-limit responses carry detail as {message, retryable}, not a string.
    const detail = err.detail;
    const msg = detail && typeof detail === 'object' ? detail.message : detail;
    throw new Error(msg || 'Failed to search nodes');
  }
  return response.json();
}

/**
 * Update settings for a single node. Returns the full state after update.
 * @param {string} path - Repo-relative node path
 * @param {Object<string, string>} settings - key:value pairs (empty value = remove key)
 * @returns {Promise<{message: string, settings: Object, tree: Object, nodeTypes: Object}>}
 */
export async function updateNodeSetting(path, settings) {
  const response = await fetchWithAuth('/api/node/setting', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ path, settings }),
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to update node setting');
  }
  return response.json();
}

/**
 * Create a new node or folder in one operation.
 * @param {string} path - e.g. "my-note" (backend adds .md for markdown)
 * @param {string} type - "markdown" | "timeline" | "checklist" | "folder"
 * @param {string} content - optional initial markdown content (ignored for folder)
 * @returns {Promise<{message: string, settings: Object, tree: Object, nodeTypes: Object}>}
 */
export async function createNode(path, type = 'markdown', content = '') {
  const response = await fetchWithAuth('/api/node/create', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ path, type, content }),
  });
  if (!response.ok) {
    const err = await response.json();
    const error = new Error(err.detail || 'Failed to create node');
    error.status = response.status;
    throw error;
  }
  return response.json();
}
